import AuthorizationError from "src/components/errors/AuthorizationError";
import ClientServerError from "src/components/errors/ServerError";
import ValidationError from "src/components/errors/ValidationError";
import { getErrorMessage } from "src/hooks/useErrorMessages";

class HttpClient {
  constructor(options = {}) {
    this._baseURL = options.baseURL || "";
    this._headers = options.headers || {};
    this._headers['content-type'] = 'application/json';
    this._headers.Authorization = `Bearer ${options.token}`
  }

  setHeader(key, value) {
    this._headers[key] = value;
    return this;
  }

  removeHeader(key) {
    delete this._headers[key];
  }

  async handleErrorResponse(res) {
    let details = {};
    try {
      details = await res.json();
    } catch (error) {
      console.warn('Failed to parse response body as JSON:', error);
      details = { Message: error };
    }
    const defaultMessage = getErrorMessage(res.status);

    switch (res.status) {
      case 400:
        throw new ValidationError(details, details?.Message, defaultMessage, res.url);
      case 401:
      case 403:
        throw new AuthorizationError(res.status, defaultMessage, res.url);
      default:
        throw new ClientServerError(details, details?.Message, defaultMessage, res.url);
    }
  }

  async _fetch(endpoint, options = {}) {
    const { parseResponse = true, ...fetchOptions } = options;
    const res = await fetch(this._baseURL + endpoint, {
      ...fetchOptions,
      headers: this._headers
    });

    if (!res.ok) {
      await this.handleErrorResponse(res);
    }

    if (parseResponse === false) {
      return res;
    }

    if (parseResponse === 'json') {
      const text = await res.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (err) {
        return {};
      }
    }

    return res;
  }

  async _fetchJSON(endpoint, options = {}) {
    return this._fetch(endpoint, { ...options, parseResponse: 'json' });
  }

  async _fetchJSONSafe(endpoint, options = {}) {
    const result = await this._fetch(endpoint, { ...options, parseResponse: 'json' });

    if (!result) return {};

    if (result && typeof result.json === 'function') {
      const contentLength = result.headers && result.headers.get && result.headers.get('content-length');
      if (result.status === 204 || contentLength === "0" || !contentLength) {
        return {};
      }
      try {
        return await result.json();
      } catch (error) {
        return {};
      }
    }

    return result;
  }

  async _fetchResponse(endpoint, options = {}) {
    return this._fetch(endpoint, options);
  }

  get(endpoint, options = {}) {
    return this._fetchJSON(
      endpoint,
      {
        ...options,
        method: 'GET'
      }
    )
  }

  put(endpoint, body, options = {}) {
    return this._fetchJSONSafe(endpoint, {
      ...options,
      body: JSON.stringify(body),
      method: "PUT"
    });
  }

  post(endpoint, body, options = {}) {
    return this._fetchJSON(
      endpoint,
      {
        ...options,
        body: JSON.stringify(body),
        method: 'POST'
      }
    )
  }

  delete(endpoint, options = {}) {
    return this._fetchJSON(
      endpoint,
      {
        parseResponse: false,
        ...options,
        method: 'DELETE'
      }
    )
  }

  async _fetchFile(endpoint, options = {}) {
    const res = await fetch(this._baseURL + endpoint, {
      ...options,
      headers: this._headers,
    });

    if (!res.ok) {
      await this.handleErrorResponse(res);
    }

    if (options.parseResponse !== false && res.status !== 204)
      return res;
    return undefined;
  }

  getFile(endpoint, options = {}) {
    return this._fetch(endpoint, {
      ...options,
      method: 'GET'
    });
  }

  upload(method, endpoint, body, options = {}) {
    this.removeHeader("content-type");
    return this._fetch(endpoint, {
      ...options,
      body: body,
      method: method
    });
  }

}

export default HttpClient